import { useState } from 'react';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useRateLimiter } from '@/hooks/useRateLimiter';
import { ProgressIndicator } from './ProgressIndicator';
import { FormSection } from './FormSection';
import { Step1Contact } from './Step1Contact';
import { Step2Organization } from './Step2Organization';
import { Step3Goals } from './Step3Goals';
import { Step4Details } from './Step4Details';
import { EscapeHatch } from './EscapeHatch';
import { SuccessState } from './SuccessState';
import { createLead, updateLead, requestCallback, completeLead } from '@/lib/leads';
import { uploadLeadFiles } from '@/lib/uploads';
import type { Step1Data, Step2Data, Step3Data, Step4Data } from '@/lib/validations';

interface FormData {
  step1?: Step1Data;
  step2?: Step2Data;
  step3?: Step3Data;
  step4?: Step4Data;
}

export function GuidedForm() {
  const { toast } = useToast();
  const { checkRateLimit, recordSubmission } = useRateLimiter({
    minInterval: 3000,  // 3 seconds between step submissions
    maxAttempts: 10,    // Max 10 step attempts
    windowMs: 300000,   // Within 5 minutes
  });
  const [leadId, setLeadId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formData, setFormData] = useState<FormData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isCallbackRequested, setIsCallbackRequested] = useState(false);

  const handleStep1Submit = async (data: Step1Data) => {
    // Check rate limit
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      toast({
        title: 'Please slow down',
        description: rateLimitCheck.message,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    recordSubmission();
    try {
      const lead = await createLead({
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
      });
      setLeadId(lead.id);
      setSessionToken(lead.session_token);
      setFormData(prev => ({ ...prev, step1: data }));
      setCompletedSteps(prev => [...prev, 1]);
      setCurrentStep(2);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save your information. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep2Submit = async (data: Step2Data) => {
    if (!leadId || !sessionToken) return;
    setIsSubmitting(true);
    try {
      await updateLead(leadId, sessionToken, {
        organization_type: data.organization_type,
        organization_name: data.organization_name,
        role_title: data.role_title,
        current_step: 2,
      });
      setFormData(prev => ({ ...prev, step2: data }));
      setCompletedSteps(prev => [...new Set([...prev, 2])]);
      setCurrentStep(3);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save your information. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep3Submit = async (data: Step3Data) => {
    if (!leadId || !sessionToken) return;
    setIsSubmitting(true);
    try {
      await updateLead(leadId, sessionToken, {
        services_interested: data.services_interested,
        primary_objective: data.primary_objective,
        referral_source: data.referral_source,
        current_step: 3,
      });
      setFormData(prev => ({ ...prev, step3: data }));
      setCompletedSteps(prev => [...new Set([...prev, 3])]);
      setCurrentStep(4);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save your information. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep4Submit = async (data: Step4Data, files: File[]) => {
    if (!leadId || !sessionToken) return;
    setIsSubmitting(true);
    try {
      if (files.length > 0) {
        try {
          await uploadLeadFiles(leadId, files);
        } catch {
          // Never lose the lead because of an upload hiccup — submit anyway.
          toast({
            title: 'Some files could not be uploaded',
            description: "Your details were submitted. We'll follow up if we need the files.",
            variant: 'destructive',
          });
        }
      }
      await completeLead(leadId, sessionToken, {
        geographic_focus: data.geographic_focus,
        target_audience: data.target_audience,
        budget_range: data.budget_range,
        timeline: data.timeline,
        additional_notes: data.additional_notes,
      });
      setFormData(prev => ({ ...prev, step4: data }));
      setCompletedSteps(prev => [...new Set([...prev, 4])]);
      setIsComplete(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit your information. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipQuestionnaire = async () => {
    if (!leadId || !sessionToken) return;
    setIsSkipping(true);
    try {
      await requestCallback(leadId, sessionToken);
      setIsCallbackRequested(true);
      toast({
        title: 'Request received!',
        description: "We'll be in touch within 24 hours.",
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process your request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSkipping(false);
    }
  };

  const handleStepClick = (step: number) => {
    if (completedSteps.includes(step)) {
      setCurrentStep(step);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  // Get first name for success state
  const firstName = formData.step1?.full_name?.split(' ')[0] || 'there';

  // Show success state
  if (isComplete || isCallbackRequested) {
    return <SuccessState firstName={firstName} />;
  }

  // Generate summaries for completed steps
  const step1Summary = formData.step1 
    ? `${formData.step1.full_name} • ${formData.step1.email} • ${formData.step1.phone}`
    : undefined;

  const step2Summary = formData.step2
    ? `${formData.step2.organization_type} • ${formData.step2.organization_name} • ${formData.step2.role_title}`
    : undefined;

  const step3Summary = formData.step3
    ? `${formData.step3.services_interested.slice(0, 2).join(', ')} • ${formData.step3.primary_objective}`
    : undefined;

  return (
    <div className="max-w-2xl mx-auto">
      <ProgressIndicator
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
      />

      <div className="space-y-4">
        <FormSection
          stepNumber={1}
          title="Contact Information"
          description="We'll use this to prepare your personalized strategy."
          isActive={currentStep === 1}
          isComplete={completedSteps.includes(1)}
          isLocked={false}
          summary={step1Summary}
          onEdit={() => setCurrentStep(1)}
        >
          <Step1Contact
            initialData={formData.step1}
            onSubmit={handleStep1Submit}
            isSubmitting={isSubmitting}
          />
        </FormSection>

        {/* Show escape hatch after step 1 is complete */}
        {completedSteps.includes(1) && currentStep > 1 && (
          <EscapeHatch onSkip={handleSkipQuestionnaire} isLoading={isSkipping} />
        )}

        <FormSection
          stepNumber={2}
          title="About Your Organization"
          description="Help us understand your organization so we can tailor our approach."
          isActive={currentStep === 2}
          isComplete={completedSteps.includes(2)}
          isLocked={!completedSteps.includes(1)}
          summary={step2Summary}
          onEdit={() => setCurrentStep(2)}
        >
          <Step2Organization
            initialData={formData.step2}
            onSubmit={handleStep2Submit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        </FormSection>

        <FormSection
          stepNumber={3}
          title="Campaign Goals & Services"
          description="What are you looking to accomplish?"
          isActive={currentStep === 3}
          isComplete={completedSteps.includes(3)}
          isLocked={!completedSteps.includes(2)}
          summary={step3Summary}
          onEdit={() => setCurrentStep(3)}
        >
          <Step3Goals
            initialData={formData.step3}
            onSubmit={handleStep3Submit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        </FormSection>

        <FormSection
          stepNumber={4}
          title="Targeting, Budget & Timeline"
          description="Final details to help us prepare your strategy."
          isActive={currentStep === 4}
          isComplete={completedSteps.includes(4)}
          isLocked={!completedSteps.includes(3)}
          onEdit={() => setCurrentStep(4)}
        >
          <Step4Details
            initialData={formData.step4}
            onSubmit={handleStep4Submit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        </FormSection>
      </div>
    </div>
  );
}
